import React, { useState, useEffect, useCallback } from "react";
import {
  Breadcrumb,
  Table,
  Input,
  Space,
  Typography,
  Tag,
  Divider,
  Spin,
  Button,
  Modal,
  Form,
  message,
} from "antd";
import axios from "axios";
import { useAuth0 } from "../../react-auth0-spa";
import config from "../../api_config.json";
import { useAppContext } from "../../context";
import { SearchOutlined, EditOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";

const { Title } = Typography;

const query = `
  query getFeatures($where: FeaturesWhereInput!, $first: Int!) {
    findFeatures(where: $where, first: $first) {
      edges {
        node {
          objectId
          name
          label
          description
          guide_article_id
          module_pointer {
            objectId
            name
          }
        }
      }
    }
  }
`
  .replace(/\r?\n|\r/g, "")
  .replace(/\t/g, " ");

const updateFeatureMutation = `
  mutation UpdateFeature($id: ID!, $fields: UpdateFeaturesFieldsInput!) {
    updateFeatures(input: { id: $id, fields: $fields }) {
      features {
        objectId
        guide_article_id
      }
    }
  }
`
  .replace(/\r?\n|\r/g, "")
  .replace(/\t/g, " ");

const Guides = () => {
  const [loading, setLoading] = useState(true);
  const [features, setFeatures] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [globalSearch, setGlobalSearch] = useState("");
  const [editingFeature, setEditingFeature] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [form] = Form.useForm();
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";
  const { getTokenSilently } = useAuth0();
  const graphqlUrl = `${config.baseUrl[env]}/graphql`;

  const fetchData = useCallback(async () => {
    try {
      const token = await getTokenSilently();
      const response = await axios({
        method: "post",
        url: graphqlUrl,
        data: {
          query,
          variables: {
            where: {
              name: { matchesRegex: "\\.show$" },
            },
            first: 10000,
          },
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const items = response.data || [];
      const sorted = items.sort((a, b) => a.name.localeCompare(b.name));
      setFeatures(sorted);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching features:", error);
      setLoading(false);
    }
  }, [getTokenSilently, graphqlUrl]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const columns = [
    {
      title: "Feature",
      dataIndex: "name",
      key: "name",
      ...getColumnSearchProps("name"),
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name) => {
        const baseName = name.replace(/\.show$/, "");
        return <strong>{baseName}</strong>;
      },
    },
    {
      title: "Label",
      dataIndex: "label",
      key: "label",
      ...getColumnSearchProps("label"),
    },
    {
      title: "Modulo",
      key: "module",
      render: (_, record) =>
        record.module_pointer ? (
          <Tag color="purple">{record.module_pointer.name}</Tag>
        ) : (
          "-"
        ),
    },
    {
      title: "Intercom Guida ID",
      dataIndex: "guide_article_id",
      key: "guide_article_id",
      sorter: (a, b) => (a.guide_article_id || 0) - (b.guide_article_id || 0),
      filters: [
        { text: "Con guida", value: "with" },
        { text: "Senza guida", value: "without" },
      ],
      onFilter: (value, record) =>
        value === "with" ? !!record.guide_article_id : !record.guide_article_id,
      render: (id) =>
        id ? (
          <Tag color="blue">{id}</Tag>
        ) : (
          <Tag color="default">-</Tag>
        ),
    },
    {
      title: "",
      key: "actions",
      width: 60,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEdit(record)}
        />
      ),
    },
  ];

  const handleEdit = (record) => {
    setEditingFeature(record);
    form.setFieldsValue({
      guide_article_id: record.guide_article_id || "",
    });
  };

  const handleModalCancel = () => {
    setEditingFeature(null);
    form.resetFields();
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      setModalLoading(true);
      const token = await getTokenSilently();

      const fields = {
        guide_article_id: values.guide_article_id
          ? Number(values.guide_article_id)
          : null,
      };

      const response = await axios({
        method: "post",
        url: graphqlUrl,
        data: {
          query: updateFeatureMutation,
          variables: { id: editingFeature.objectId, fields },
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.errors) {
        throw new Error(response.data.errors.map((e) => e.message).join(", "));
      }

      message.success("Guida aggiornata con successo");
      setEditingFeature(null);
      form.resetFields();
      fetchData();
    } catch (error) {
      console.error("Error updating guide:", error);
      message.error(error.message || "Errore durante il salvataggio");
    } finally {
      setModalLoading(false);
    }
  };

  return (
    <div style={{ margin: "16px 16px" }}>
      <Spin spinning={loading}>
        <Breadcrumb style={{ marginBottom: "20px" }}>
          <Breadcrumb.Item>Configurazioni</Breadcrumb.Item>
          <Breadcrumb.Item>Guide</Breadcrumb.Item>
        </Breadcrumb>
        <Divider />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "40px",
          }}
        >
          <Title level={3}>Guide</Title>
          <Input
            placeholder="Cerca per nome, label o modulo..."
            prefix={<SearchOutlined />}
            value={globalSearch}
            onChange={(e) => setGlobalSearch(e.target.value)}
            allowClear
            style={{ width: 350 }}
          />
        </div>
        <Table
          dataSource={features.filter((f) => {
            if (!globalSearch) return true;
            const s = globalSearch.toLowerCase();
            return (
              f.name?.toLowerCase().includes(s) ||
              f.label?.toLowerCase().includes(s) ||
              f.module_pointer?.name?.toLowerCase().includes(s)
            );
          })}
          columns={columns}
          loading={loading}
          size="small"
          pagination={false}
          rowKey="objectId"
        />
        <Modal
          visible={!!editingFeature}
          title={`Modifica Guida - ${editingFeature?.name?.replace(/\.show$/, "") || ""}`}
          okText="Salva"
          cancelText="Annulla"
          onCancel={handleModalCancel}
          confirmLoading={modalLoading}
          onOk={handleModalOk}
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="guide_article_id"
              label="Intercom Guida ID"
            >
              <Input type="number" placeholder="ID articolo guida Intercom" />
            </Form.Item>
          </Form>
        </Modal>
      </Spin>
    </div>
  );
};

export default Guides;
